import React, { useState } from 'react';
import { WizardStepProps } from '@components/Wizard/Wizard';
import PINInput from '@components/PINInput/PINInput';
import BiometricsInitiationBottomSheet from '@components/BiometricsInitiationBottomSheet/BiometricsInitiationBottomSheet';
import { useBiometricsStore } from '@stores/biometricsStore';
import { TonWalletUtils } from '@lib/secrets';
import LoadingModal from '@components/LoadingModal/LoadingModal';
import { Biometrics } from '@lib/telegram';
import { useTranslation } from 'react-i18next';
import { ModalWithIcon } from '@components/ModalWithIcon/ModalWithIcon';
import ReactDOM from 'react-dom';
import { delay, redirectToTelegramSupport } from '@lib/utils';
import SlideCaptchaModal from '@components/SlideCaptcha/SlideCaptchaModal';
import { CaptchaRequiredResponse } from '../../../types/captcha';
import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router';
import { ApiError } from '@lib/api';

interface LoginPINInputStepProps extends WizardStepProps {
  walletData?: {mnemonic: string, secretKey: Uint8Array} | null;
}

const PINInputStep: React.FC<LoginPINInputStepProps> = ({ onNext, walletData }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isBiometricsPromptVisible, setIsBiometricsPromptVisible] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const { setBiometrics } = useBiometricsStore();
  const [showModal, setShowModal] = useState(false)
  const [modalTitle, setModalTitle] = useState('')
  const [modalDescription, setModalDescription] = useState('')
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaData, setCaptchaData] = useState<CaptchaRequiredResponse | null>(null);
  const [captchaError, setCaptchaError] = useState<string>('');
  const [currentPin, setCurrentPin] = useState<string>('');
  const proceedAfterLogin = () => {
    if (Biometrics.isBiometricsInited() && Biometrics.isAvailable()) {
      setIsBiometricsPromptVisible(true);
    } else {
      onNext?.();
    }
  };

  const handlePinComplete = async (result: { success: boolean; newPin?: string }) => {
    if (result.success && result.newPin && walletData) {
      setIsLoading(true);
      try {
        // Store PIN in component state for captcha flow
        setCurrentPin(result.newPin);
        
        // Create complete wallet data with PIN
        const completeWalletData = {
          pin: result.newPin,
          mnemonic: walletData.mnemonic,
          secretKey: walletData.secretKey
        };
        
        await delay(1000);
        
        // Authenticate using temporary wallet data (don't store yet)
        const loginResult = await TonWalletUtils.loginWalletWithData(completeWalletData);

        if (loginResult && 'captcha_required' in loginResult) {
          // Captcha is required for login - don't store wallet data yet
          setCaptchaData(loginResult);
          setShowCaptcha(true);
          setIsLoading(false);
        } else {
          // Login successful, now store wallet data
          await TonWalletUtils.storeWalletData(completeWalletData);
          setIsLoading(false);
          proceedAfterLogin();
        }
      } catch (error) {
        Sentry.captureException(error);
        await TonWalletUtils.clear();
        await TonWalletUtils.clearToken();
        setIsLoading(false);
        setModalTitle(t('page.loginWallet.steps.pin.errorText'));
        setModalDescription(t('page.loginWallet.steps.pin.errorTextDescription'));
        setShowModal(true);
      }
    } else if (!walletData) {
      setModalTitle(t('page.loginWallet.steps.pin.errorText'));
      setModalDescription('Wallet data not found. Please go back and try again.');
      setShowModal(true);
    }
  };

  const handleCaptchaSuccess = async (captcha_key: string, slide_x: number, slide_y: number): Promise<boolean> => {
    setIsLoading(true);
    setCaptchaError('');

    try {
      if (!walletData) {
        setCaptchaError('Wallet data not found. Please try again.');
        setIsLoading(false);
        return false;
      }

      // Use PIN from component state
      if (!currentPin) {
        setCaptchaError('PIN not found. Please try again.');
        setIsLoading(false);
        return false;
      }

      const completeWalletData = {
        pin: currentPin,
        mnemonic: walletData.mnemonic,
        secretKey: walletData.secretKey
      };

      const loginResult = await TonWalletUtils.loginWalletWithData(completeWalletData, captcha_key, slide_x, slide_y);

      if (loginResult && 'captcha_required' in loginResult) {
        // Captcha failed, show new captcha
        setCaptchaData(loginResult);
        setIsLoading(false);
        return false;
      } else {
        // Login successful, now store wallet data
        await TonWalletUtils.storeWalletData(completeWalletData);
        setShowCaptcha(false);
        proceedAfterLogin();
        setIsLoading(false);
        return true;
      }
    } catch (error) {
      // Extract error message
      let errorMsg = t('errors.genericServerDescription');
      if (error instanceof ApiError) {
        const translationKey = `errors.${error.errorCode}.description`;
        const translatedMessage = t(translationKey);
        errorMsg = translatedMessage !== translationKey
          ? translatedMessage
          : t('errors.genericServerDescription');
      }

      // Dismiss captcha modal
      setShowCaptcha(false);
      setCaptchaData(null);
      setCaptchaError('');
      setIsLoading(false);

      // Show error in modal
      setModalTitle(t('page.loginWallet.steps.pin.errorText'));
      setModalDescription(errorMsg);
      setShowModal(true);

      return false;
    }
  };

  const handleCaptchaClose = () => {
    setShowCaptcha(false);
    setCaptchaData(null);
    setCaptchaError('');
    setIsLoading(false);
  };

  const handleCaptchaOverlayClose = () => {
    handleCaptchaClose();
    // Navigate back to previous page
    navigate(-1);
  };

  const handleBiometricsEnable = () => {
    setBiometrics(true);
    handleNext();
  }

  const handleNext = () => {
    setIsBiometricsPromptVisible(false);
    onNext?.();
  }

  return (
    <>
      <PINInput 
        mode="create" 
        onComplete={handlePinComplete}
      />

      {ReactDOM.createPortal(
        <ModalWithIcon
            title={modalTitle}
            description={modalDescription}
            onPositive={() => {
              redirectToTelegramSupport('JagadCS_bot');
              setShowModal(false);
            }}
            positiveText={t('page.loginWallet.steps.pin.errorButtonText')}
            type="failed"
            visible={showModal}
            onModalOverlayClick={() => setShowModal(false)}
        />,
      document.body)}

      <LoadingModal visible={isLoading} />
      <BiometricsInitiationBottomSheet 
        isOpen={isBiometricsPromptVisible}
        onSkip={handleNext}
        onEnable={handleBiometricsEnable}
      />
      <SlideCaptchaModal
        isOpen={showCaptcha}
        onClose={handleCaptchaClose}
        onOverlayClose={handleCaptchaOverlayClose}
        onSuccess={handleCaptchaSuccess}
        captchaData={captchaData}
        error={captchaError}
      />
    </>
  );
};

export default PINInputStep;
